import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { setSidebarOverride } from '@/store/sidebar-override.slice'
import { formatPhone } from '@/lib/phone'
import { formatPropertyAddress } from '@/lib/property-address'
import { AlertCircleIcon, ArrowUpDownIcon, ChevronLeftIcon, EllipsisVerticalIcon, PencilIcon, PlusIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Avatar, AvatarFallback,
	Badge,
	Button,
	ConfirmDialog,
	Dialog, DialogContent, DialogTitle,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Switch,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
	Tabs, TabsList, TabsTrigger,
	Textarea,
} from '@rentflowdev/ui'
import { useTranslation } from 'react-i18next'
import {
	useAddContactNoteMutation, useCreateDocumentMutation, useDeleteContactMutation, useDeleteContactNoteMutation,
	useDeleteDocumentMutation, useListDocumentTemplatesQuery,
	useUpdateDocumentStatusMutation,
	useGetContactQuery, useListContactDocumentsQuery, useListContactNotesQuery,
	useListDealsQuery, useListPropertiesQuery, useListRentalsQuery, useUpdateContactMutation,
} from '@/api'
import type { Contact, Deal, DocumentStatus, DocumentTemplate, DocumentType, Property, RegistryDocument, RentalStatus } from '@/types'
import { extractErrorMessage } from '@/lib/error-message'
import { DEFAULT_LOCALE, DOCUMENT_STATUS_KEYS, DOCUMENT_STATUS_VARIANTS, DOCUMENT_STATUSES, DOCUMENT_TYPE_KEYS, RENTAL_STATUS_KEYS } from '@/types'
import { ChangeReliabilityDialog } from '@/components/change-reliability-dialog'
import { CreateDocumentWizard } from '@/components/create-document-wizard'
import type { CreateDocumentPayload } from '@/components/create-document-wizard/types'
import { ConversationChat } from '@/components/conversation-chat'
import { EditContactDialog } from '@/components/edit-contact-dialog'
import { SelectTemplateDialog } from '@/components/select-template-dialog'
import { StartRentalDialog } from '@/components/start-rental-dialog'
import { TenantKycSection } from '@/components/tenant-kyc-section'

export const Route = createFileRoute('/_app/tenants/$tenantId')({
	component: TenantDetailPage,
	// The tab lives in the URL so that returning from a document lands on
	// the tab the document was opened from, not on the default one.
	validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
		tab: typeof search.tab === 'string' ? search.tab : undefined,
	}),
})

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatObjectType(p: Property, t: (key: string) => string): string {
	const base = t(`properties.types.${p.type}`)
	if (p.type === 'APARTMENT' && p.rooms) return `${p.rooms}-к ${base.toLowerCase()}`
	return base
}

function buildPropertyLabel(p: Property, t: (key: string, opts?: Record<string, unknown>) => string): string {
	const typeLabel = p.rooms
		? `${t('properties.roomsSuffix', { rooms: p.rooms })} ${t(`properties.types.${p.type}`).toLowerCase()}`
		: t(`properties.types.${p.type}`)
	return `${typeLabel} ${formatPropertyAddress(p.city, p.address)}`
}

type RentalPeriodStatus = 'past' | 'current' | 'upcoming'

function getRentalStatus(deal: Deal): RentalPeriodStatus {
	const now = Date.now()
	const start = deal.startDate ? new Date(deal.startDate).getTime() : 0
	const end = deal.endDate ? new Date(deal.endDate).getTime() : Infinity
	if (deal.startDate && deal.endDate) {
		if (end < now) return 'past'
		if (start > now) return 'upcoming'
		return 'current'
	}
	if (deal.status === 'ACTIVE') return 'upcoming'
	if (deal.status === 'CANCELLED' || deal.status === 'COMPLETED') return 'past'
	return 'current'
}

const RENTAL_STATUS_VARIANTS: Record<RentalPeriodStatus, 'secondary' | 'success' | 'info'> = {
	past: 'secondary',
	current: 'success',
	upcoming: 'info',
}

const RENTAL_STATUS_BADGE_VARIANTS: Record<RentalStatus, 'secondary' | 'success' | 'info' | 'warning'> = {
	ACTIVE:     'success',
	UPCOMING:   'info',
	CLOSING:    'warning',
	COMPLETED:  'secondary',
}

/** Actual rentals of the tenant (Rental entities, not Deals). */
function TenantRentalsSection({ tenantId, t }: {
	tenantId: string
	t: (key: string, opts?: Record<string, unknown>) => string
}) {
	const navigate = useNavigate()
	// Server-side filtering — gateway GET /rent supports tenant_contact_id.
	const { data } = useListRentalsQuery({ tenantContactId: tenantId })
	const rentals = data?.rentals ?? []

	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-base font-[500] leading-6 text-(--color-foreground)">
				{t('tenants.rentals.title')}
			</h2>
			{rentals.length === 0 ? (
				<p className="text-sm text-(--color-muted-foreground)">{t('tenants.rentals.empty')}</p>
			) : (
				<div className="overflow-hidden rounded-lg border border-(--color-border)">
					<Table>
						<TableBody>
							{rentals.map((r) => (
								<TableRow
									key={r.id}
									className="h-16 cursor-pointer"
									onClick={() => void navigate({ to: '/rent/$rentalId', params: { rentalId: r.id } })}
								>
									<TableCell className="px-4">
										<div className="flex flex-col gap-0.5">
											<span className="text-sm font-[500] text-(--color-info) underline-offset-2 hover:underline">
												{r.propertyAddress ?? r.propertyTitle ?? `Аренда №${r.id.slice(-6)}`}
											</span>
											<span className="text-sm text-(--color-muted-foreground)">
												{formatDate(r.startDate)} — {r.endDate ? formatDate(r.endDate) : '…'}
											</span>
										</div>
									</TableCell>
									<TableCell className="w-[160px] px-4">
										<Badge variant={RENTAL_STATUS_BADGE_VARIANTS[r.status]} size="sm">
											{t(RENTAL_STATUS_KEYS[r.status])}
										</Badge>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</section>
	)
}

function TenantHistorySection({ deals, propertiesMap, t }: {
	deals: Deal[]
	propertiesMap: Map<string, Property>
	t: (key: string, opts?: Record<string, unknown>) => string
}) {
	const navigate                          = useNavigate()
	const [statusFilter, setStatusFilter] = useState('')
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

	const rows = useMemo(() => {
		let filtered = deals
		if (statusFilter) {
			filtered = filtered.filter((d) => getRentalStatus(d) === statusFilter)
		}
		const dir = sortDir === 'asc' ? 1 : -1
		return [...filtered].sort((a, b) =>
			(a.startDate ?? a.createdAt).localeCompare(b.startDate ?? b.createdAt) * dir
		)
	}, [deals, statusFilter, sortDir])

	return (
		<div className="flex flex-col gap-4">
			<div className="flex gap-2">
				<Select disabled>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('tenants.history.filterProperty')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('tenants.filters.allProperties')}</SelectItem>
					</SelectContent>
				</Select>
				<Select disabled>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('tenants.history.filterPeriod')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('tenants.filters.allPeriods')}</SelectItem>
					</SelectContent>
				</Select>
				<Select
					value={statusFilter || 'all'}
					onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
				>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('tenants.history.filterStatus')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('tenants.history.filterStatus')}</SelectItem>
						<SelectItem value="current">{t('tenants.history.statuses.current')}</SelectItem>
						<SelectItem value="upcoming">{t('tenants.history.statuses.upcoming')}</SelectItem>
						<SelectItem value="past">{t('tenants.history.statuses.past')}</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<div className="overflow-hidden rounded-lg border border-(--color-border)">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead>
								<Button
									variant="ghost"
									iconRight={<ArrowUpDownIcon className="size-3.5" />}
									onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')}
									className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
								>
									{t('tenants.history.columnRental')}
								</Button>
							</TableHead>
							<TableHead className="w-[160px] font-[500] text-(--color-muted-foreground)">
								{t('tenants.history.columnStatus')}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map((d) => {
							const prop = d.propertyId ? propertiesMap.get(d.propertyId) : undefined
							const status = getRentalStatus(d)
							return (
								<TableRow
									key={d.id}
									className="h-16 cursor-pointer"
									onClick={() => void navigate({ to: '/deals/$dealId', params: { dealId: d.id } })}
								>
									<TableCell className="px-4">
										<div className="flex flex-col gap-0.5">
											<span className="text-sm font-[500] text-(--color-info) underline-offset-2 hover:underline">
												{prop ? buildPropertyLabel(prop, t) : '—'}
											</span>
											<span className="text-sm text-(--color-muted-foreground)">
												{d.startDate && d.endDate
													? `${formatDate(d.startDate)} - ${formatDate(d.endDate)}`
													: '—'}
											</span>
										</div>
									</TableCell>
									<TableCell className="w-[160px] px-4">
										<Badge variant={RENTAL_STATUS_VARIANTS[status]} size="sm">
											{t(`tenants.history.statuses.${status}`)}
										</Badge>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}

const TENANT_DOC_TYPES: DocumentType[] = ['LEASE_CONTRACT', 'ACT_TRANSFER', 'ACT_RETURN', 'APPENDIX', 'REGISTRATION_APPLICATION', 'OTHER']

function TenantDocumentsSection({ tenantId, deals, propertiesMap, t }: {
	tenantId: string
	deals: Deal[]
	propertiesMap: Map<string, Property>
	t: (key: string, opts?: Record<string, unknown>) => string
}) {
	const navigate                           = useNavigate()
	const currentHref                        = useRouterState({ select: (st) => st.location.href })
	const [typeFilter,   setTypeFilter]   = useState<DocumentType | 'all'>('all')
	const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all')
	const [rentalFilter, setRentalFilter] = useState<string>('all')
	const [selectOpen,   setSelectOpen]   = useState(false)
	const [chosenTemplate, setChosenTemplate] = useState<DocumentTemplate | null>(null)
	const [fillOpen, setFillOpen] = useState(false)

	const { data, isLoading } = useListContactDocumentsQuery({
		entityId: tenantId,
		type:     typeFilter   === 'all' ? undefined : typeFilter,
		status:   statusFilter === 'all' ? undefined : statusFilter,
	})
	const { data: tplData } = useListDocumentTemplatesQuery({ page: 1, limit: 50 })
	const [deleteDocument]   = useDeleteDocumentMutation()
	const [updateStatus]     = useUpdateDocumentStatusMutation()
	const [createDocument]   = useCreateDocumentMutation()

	const docs: RegistryDocument[] = data?.documents ?? []
	const templates = tplData?.templates ?? []

	// Deal selected for document creation (null until user picks one)
	const [activeDeal,      setActiveDeal]      = useState<Deal | null>(null)
	const [dealPickerOpen,  setDealPickerOpen]  = useState(false)

	// Property overrides resolved from the selected deal
	const activeProp = activeDeal?.propertyId ? propertiesMap.get(activeDeal.propertyId) : undefined

	// Build «Аренда» options for the filter select
	const rentalOptions = useMemo(
		() => deals.map((d) => {
			const prop  = d.propertyId ? propertiesMap.get(d.propertyId) : undefined
			const title = prop ? buildPropertyLabel(prop, t) : t('tenants.documents.unknownRental')
			const range = (d.startDate && d.endDate)
				? `${formatDate(d.startDate)} — ${formatDate(d.endDate)}`
				: ''
			return { id: d.id, label: range ? `${title} · ${range}` : title }
		}),
		[deals, propertiesMap, t],
	)

	function handleCreateClick() {
		if (deals.length === 0) return
		if (deals.length === 1) {
			setActiveDeal(deals[0])
			setSelectOpen(true)
		} else {
			setDealPickerOpen(true)
		}
	}

	function handleDealPicked(deal: Deal) {
		setActiveDeal(deal)
		setDealPickerOpen(false)
		setSelectOpen(true)
	}

	function handleTemplateConfirmed(tpl: DocumentTemplate) {
		setChosenTemplate(tpl)
		setSelectOpen(false)
		setFillOpen(true)
	}

	async function handleDocumentSaved(payload: {
		template: DocumentTemplate
		title:    string
		data:     CreateDocumentPayload
	}) {
		const doc = await createDocument({
			templateId:     payload.template.id,
			type:           (payload.data.documentType ?? payload.template.type) as DocumentType,
			title:          payload.title,
			entityType:     'CONTACT',
			entityId:       tenantId,
			filledDataJson: JSON.stringify(payload.data),
		}).unwrap()
		if (doc?.id) {
			await updateStatus({ id: doc.id, body: { status: 'DATA_FILLED' } }).unwrap()
		}
	}

	if (isLoading) return <Spinner size="sm" />

	return (
		<>
		<div className="flex flex-col gap-4">
			<div className="flex items-center gap-2">
				<Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as DocumentType | 'all')}>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('tenants.documents.filterType')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('tenants.documents.allTypes')}</SelectItem>
						{TENANT_DOC_TYPES.map((tp) => (
							<SelectItem key={tp} value={tp}>{t(DOCUMENT_TYPE_KEYS[tp])}</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as DocumentStatus | 'all')}>
					<SelectTrigger width="md" tone="muted">
						<SelectValue placeholder={t('tenants.documents.filterStatus')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('tenants.documents.allStatuses')}</SelectItem>
						{DOCUMENT_STATUSES.map((s) => (
							<SelectItem key={s} value={s}>{t(DOCUMENT_STATUS_KEYS[s])}</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select value={rentalFilter} onValueChange={setRentalFilter}>
					<SelectTrigger width="lg" tone="muted">
						<SelectValue placeholder={t('tenants.documents.filterRental')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('tenants.documents.allRentals')}</SelectItem>
						{rentalOptions.map((r) => (
							<SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Button variant="secondary" size="sm" iconLeft={<PlusIcon className="size-4" />} className="ml-auto" disabled={deals.length === 0} onClick={handleCreateClick}>
					{t('deals.documents.create')}
				</Button>
			</div>

			{docs.length === 0 ? (
				<p className="text-sm text-(--color-muted-foreground)">{t('deals.documents.empty')}</p>
			) : (
				<div className="overflow-hidden rounded-lg border border-(--color-border)">
					<Table>
						<TableHeader>
							<TableRow className="hover:bg-transparent">
								<TableHead className="font-[500] text-(--color-muted-foreground) px-4">
									{t('tenants.documents.columnDocument')}
								</TableHead>
								<TableHead className="w-[160px] font-[500] text-(--color-muted-foreground) px-4">
									{t('deals.documents.columns.status')}
								</TableHead>
								<TableHead className="w-[48px]" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{docs.map((doc) => (
								<TableRow
									key={doc.id}
									className="h-16 cursor-pointer"
									onClick={() => void navigate({ to: '/documents/$documentId', params: { documentId: doc.id }, search: { from: currentHref } })}
								>
									<TableCell className="px-4 text-sm text-(--color-foreground)">
										{doc.title}
									</TableCell>
									<TableCell className="px-4">
										<Badge variant={DOCUMENT_STATUS_VARIANTS[doc.status as DocumentStatus]} size="sm">
											{t(DOCUMENT_STATUS_KEYS[doc.status as DocumentStatus])}
										</Badge>
									</TableCell>
									<TableCell className="w-[48px] px-4" onClick={(e) => e.stopPropagation()}>
										<DropdownMenu>
											<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('common.delete')} />}>
												<EllipsisVerticalIcon className="size-4" />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="min-w-40">
												<DropdownMenuItem
													disabled={!doc.fileUrl}
													onClick={() => doc.fileUrl && window.open(doc.fileUrl, '_blank')}
												>
													{t('deals.documents.actions.download')}
												</DropdownMenuItem>
												<DropdownMenuItem
													disabled={doc.status !== 'DATA_FILLED'}
													onClick={() => void updateStatus({ id: doc.id, body: { status: 'SIGNED_BY_ME' } })}
												>
													{t('deals.documents.actions.sign')}
												</DropdownMenuItem>
												<DropdownMenuItem
													disabled={doc.status !== 'SIGNED_BY_ME'}
													onClick={() => void updateStatus({ id: doc.id, body: { status: 'SENT' } })}
												>
													{t('deals.documents.actions.send')}
												</DropdownMenuItem>
												<DropdownMenuItem
													className="text-(--color-destructive) focus:text-(--color-destructive)"
													onClick={() => void deleteDocument(doc.id)}
												>
													{t('common.delete')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>

		<SelectTemplateDialog
			open={selectOpen}
			templates={templates}
			onOpenChange={setSelectOpen}
			onConfirm={handleTemplateConfirmed}
		/>

		{activeDeal && (
			<CreateDocumentWizard
				open={fillOpen}
				template={chosenTemplate}
				deal={activeDeal}
				property={activeProp ? {
					objectType:      formatObjectType(activeProp, t),
					address:         activeProp.address,
					cadastralNumber: activeProp.cadastralNumber,
					area:            activeProp.area,
					rentAmount:      activeProp.rentTerms?.rentAmount,
					rentCurrency:    activeProp.rentTerms?.rentCurrency,
					depositAmount:   activeProp.rentTerms?.depositAmount,
					penaltyRate:     activeProp.rentTerms?.penaltyRate,
					vatPercent:      activeProp.rentTerms?.vatPercent,
					vatIncluded:     activeProp.rentTerms?.vatIncluded,
					vatClause:       activeProp.rentTerms?.vatClause,
				} : undefined}
				existingNumbers={docs.map((d) => d.title.replace(/\D+/g, '')).filter(Boolean)}
				onOpenChange={(v) => {
					setFillOpen(v)
					if (!v) { setChosenTemplate(null); setActiveDeal(null) }
				}}
				onSave={handleDocumentSaved}
			/>
		)}

		<Dialog open={dealPickerOpen} onOpenChange={setDealPickerOpen}>
			<DialogContent className="sm:max-w-[24rem] p-0 gap-0 rounded-lg overflow-hidden">
				<div className="flex items-center border-b border-(--color-border) p-5">
					<DialogTitle className="text-lg font-[500] text-(--color-foreground)">
						Выберите сделку
					</DialogTitle>
				</div>
				<div className="flex flex-col p-2">
					{deals.map((d) => {
						const prop  = d.propertyId ? propertiesMap.get(d.propertyId) : undefined
						const label = prop ? buildPropertyLabel(prop, t) : t('tenants.documents.unknownRental')
						const dates = d.startDate
							? `${formatDate(d.startDate)}${d.endDate ? ` — ${formatDate(d.endDate)}` : ''}`
							: ''
						return (
							<button
								key={d.id}
								type="button"
								className="flex flex-col gap-0.5 rounded px-3 py-2.5 text-left hover:bg-(--color-muted) transition-colors"
								onClick={() => handleDealPicked(d)}
							>
								<span className="text-sm font-[500] text-(--color-foreground)">{label}</span>
								{dates && <span className="text-xs text-(--color-muted-foreground)">{dates}</span>}
							</button>
						)
					})}
				</div>
			</DialogContent>
		</Dialog>
	</>
	)
}

function formatNoteDate(iso: string): string {
	const d = new Date(iso)
	const now = new Date()
	const isToday = d.toDateString() === now.toDateString()
	const time = d.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	if (isToday) return `Сегодня, ${time}`
	return `${d.toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${time}`
}

function parseNote(text: string): { title: string; body?: string } {
	const nl = text.indexOf('\n')
	if (nl === -1) return { title: text }
	return { title: text.slice(0, nl), body: text.slice(nl + 1).trim() || undefined }
}

function TenantNotesSection({ tenantId }: { tenantId: string }) {
	const { t } = useTranslation()
	const { data: notesData } = useListContactNotesQuery(tenantId)
	const [addNote, { isLoading: isAdding }] = useAddContactNoteMutation()
	const [deleteNote] = useDeleteContactNoteMutation()
	const [draft, setDraft] = useState('')
	const [actionError, setActionError] = useState<{ title: string; description?: string } | null>(null)

	const notes = notesData?.items ?? []

	async function handleAdd() {
		const text = draft.trim()
		if (!text) return
		setActionError(null)
		try {
			await addNote({ contactId: tenantId, body: { text } }).unwrap()
			setDraft('')
		} catch (err) {
			setActionError({ title: t('tenants.notes.errors.create'), description: extractErrorMessage(err) })
		}
	}

	return (
		<div className="flex flex-col gap-3">
			{actionError && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{actionError.title}</AlertTitle>
					{actionError.description && <AlertDescription>{actionError.description}</AlertDescription>}
				</Alert>
			)}
			<div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--color-border) p-3">
				<Textarea
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					placeholder={t('tenants.notes.placeholder')}
					className="min-h-20"
				/>
				<div className="flex justify-end">
					<Button
						size="sm"
						disabled={isAdding || !draft.trim()}
						onClick={() => void handleAdd()}
					>
						{t('tenants.notes.add')}
					</Button>
				</div>
			</div>

			{notes.map((note) => {
				const { title, body } = parseNote(note.text)
				return (
					<div key={note.id} className="flex flex-col gap-2 rounded-(--radius-md) border border-(--color-border) p-4">
						<div className="flex items-start justify-between gap-2">
							<p className="text-sm font-[500] text-(--color-foreground)">{title}</p>
							<DropdownMenu>
								<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" className="shrink-0" aria-label={t('common.delete')} />}>
									<EllipsisVerticalIcon className="size-4" />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="min-w-40">
									<DropdownMenuItem
										className="text-(--color-destructive) focus:text-(--color-destructive)"
										onClick={() => void deleteNote({ contactId: tenantId, noteId: note.id })}
									>
										{t('common.delete')}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
						{body && (
							<p className="text-sm text-(--color-foreground) leading-5">{body}</p>
						)}
						<div className="flex items-center gap-2 text-sm text-(--color-muted-foreground)">
							<Avatar className="size-5">
								<AvatarFallback className="text-[10px]">
									{(note.authorName ?? '').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<span>{note.authorName ?? ''}</span>
							<span className="ml-auto">{formatNoteDate(note.createdAt)}</span>
						</div>
					</div>
				)
			})}
		</div>
	)
}

function TenantMobileAccessSection({ contact, t }: {
	contact: Contact
	t: (key: string, opts?: Record<string, unknown>) => string
}) {
	const [updateContact, { isLoading }] = useUpdateContactMutation()
	const [accessError, setAccessError] = useState<string | null>(null)

	const hasEmail = Boolean(contact.email)
	// The API layer applies an optimistic cache patch for mobileAccessEnabled,
	// so the switch flips instantly and reverts automatically on failure.
	const enabled = contact.mobileAccessEnabled

	async function handleToggle(next: boolean) {
		setAccessError(null)
		try {
			await updateContact({ id: contact.id, body: { mobileAccessEnabled: next } }).unwrap()
		} catch (err) {
			setAccessError(extractErrorMessage(err) ?? '')
		}
	}

	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-base font-[500] leading-6 text-(--color-foreground)">{t('tenants.mobileAccess.title')}</h2>
			<div className="flex items-center justify-between gap-4 rounded-(--radius-md) border border-(--color-border) p-4">
				<div className="flex flex-col gap-1">
					<span className="text-sm text-(--color-foreground)">{t('tenants.mobileAccess.label')}</span>
					{!hasEmail && (
						<span className="text-xs text-(--color-muted-foreground)">
							{t('tenants.mobileAccess.helperNoEmail')}
						</span>
					)}
					{hasEmail && enabled && (
						<span className="text-xs text-(--color-muted-foreground)">
							{t('tenants.mobileAccess.helperEnabled', { email: contact.email })}
						</span>
					)}
				</div>
				<Switch
					checked={enabled}
					disabled={!hasEmail || isLoading}
					aria-label={t('tenants.mobileAccess.title')}
					onCheckedChange={(v) => void handleToggle(v === true)}
				/>
			</div>
			{accessError !== null && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{t('tenants.mobileAccess.error')}</AlertTitle>
					{accessError && <AlertDescription>{accessError}</AlertDescription>}
				</Alert>
			)}
		</section>
	)
}

function TenantDetailPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { tenantId } = Route.useParams()
	const { data: contact, isLoading } = useGetContactQuery(tenantId)
	const { data: dealsData } = useListDealsQuery()
	const { data: propertiesData } = useListPropertiesQuery()
	const [deleteContact] = useDeleteContactMutation()
	const { tab: tabParam } = Route.useSearch()
	const tab = tabParam ?? 'main'
	const setTab = (next: string) => {
		void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, tab: next }), replace: true })
	}
	const [chatCollapsed, setChatCollapsed] = useState(false)
	const [editOpen,        setEditOpen]        = useState(false)
	const [statusOpen,      setStatusOpen]      = useState(false)
	const [deleteOpen,      setDeleteOpen]      = useState(false)
	const [startRentalOpen, setStartRentalOpen] = useState(false)

	const allDeals = dealsData?.items ?? []
	const tenantDeals = allDeals.filter((d: Deal) => d.contactId === tenantId)
	const deal = tenantDeals[0]
	const dispatch = useDispatch()

	useEffect(() => {
		if (!deal) {
			dispatch(setSidebarOverride('/tenants/current'))
		} else {
			const period = getRentalStatus(deal)
			const childPath =
				period === 'past'     ? '/tenants/past' :
				period === 'upcoming' ? '/tenants/upcoming' :
				                        '/tenants/current'
			dispatch(setSidebarOverride(childPath))
		}
		return () => { dispatch(setSidebarOverride(null)) }
	}, [deal, dispatch])

	const propertiesMap = useMemo(() => {
		const map = new Map<string, Property>()
		for (const p of propertiesData?.properties ?? []) map.set(p.id, p)
		return map
	}, [propertiesData])
	const property = deal?.propertyId ? propertiesMap.get(deal.propertyId) : undefined

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner />
			</div>
		)
	}

	if (!contact) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-(--color-muted-foreground)">{t('tenants.notFound')}</p>
			</div>
		)
	}

	return (
		<div className="relative flex h-full flex-col overflow-hidden">
			<div className="flex flex-col gap-3 border-b border-(--color-border) px-4 py-3">
				<div className="flex items-start justify-between gap-2">
					<div className="flex items-center gap-2">
						<h1 className="text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground)">
							{contact.name}
						</h1>
						{contact.status && (
							<Badge
								variant={
									contact.status === 'POSITIVE'
										? 'success'
										: contact.status === 'NEGATIVE'
										? 'destructive'
										: 'warning'
								}
								size="sm"
							>
								{t(`tenants.reliability.${contact.status.toLowerCase()}`)}
							</Badge>
						)}
					</div>
					<div className="flex items-center gap-2">
						<Button size="sm" onClick={() => setStartRentalOpen(true)}>
							{t('tenants.actions.startRental')}
						</Button>
						<DropdownMenu>
						<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('tenants.actions.changeStatus')} />}>
							<EllipsisVerticalIcon className="size-4" />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="min-w-40">
							<DropdownMenuItem onClick={() => setEditOpen(true)}>
								{t('common.edit')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setStatusOpen(true)}>
								{t('tenants.actions.changeStatus')}
							</DropdownMenuItem>
							<DropdownMenuItem
								className="text-(--color-destructive) focus:text-(--color-destructive)"
								onClick={() => setDeleteOpen(true)}
							>
								{t('tenants.actions.delete')}
							</DropdownMenuItem>
						</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				<Tabs value={tab} onValueChange={setTab}>
					<TabsList variant="underline">
						<TabsTrigger variant="underline" value="main">
							{t('tenants.detail.tabs.main')}
							<span
								role="button"
								tabIndex={0}
								aria-label={t('common.edit')}
								onClick={(e) => { e.stopPropagation(); setEditOpen(true) }}
								onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditOpen(true) } }}
								className="ml-1 inline-flex cursor-pointer items-center rounded-(--radius-sm) p-0.5 hover:bg-(--color-muted)"
							>
								<PencilIcon className="size-3.5" />
							</span>
						</TabsTrigger>
						<TabsTrigger variant="underline" value="history">{t('tenants.detail.tabs.history')}</TabsTrigger>
						<TabsTrigger variant="underline" value="documents">{t('tenants.detail.tabs.documents')}</TabsTrigger>
						<TabsTrigger variant="underline" value="notes">
							{t('tenants.detail.tabs.notes')}
							<PlusIcon className="ml-1 size-3.5" />
						</TabsTrigger>
						<TabsTrigger variant="underline" value="kyc">{t('tenants.detail.tabs.kyc')}</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			<div className="grid flex-1 grid-cols-[1fr_440px] gap-4 overflow-hidden px-4 py-4">
				<div className="overflow-auto">
					{tab === 'main' && (
						<div className="flex flex-col gap-6">
							<section className="flex flex-col gap-3">
								<h2 className="text-base font-[500] leading-6 text-(--color-foreground)">{t('tenants.detail.profile')}</h2>
								<div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-2 rounded-(--radius-md) border border-(--color-border) p-4">
									<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.fullName')}:</span>
									<span className="text-sm text-(--color-foreground)">{contact.name}</span>
									<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.phone')}:</span>
									<span className="text-sm text-(--color-foreground)">{formatPhone(contact.phone) || t('tenants.detail.notSpecified')}</span>
									<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.email')}:</span>
									<span className="text-sm text-(--color-foreground)">{contact.email ?? t('tenants.detail.notSpecified')}</span>
									<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.company')}:</span>
									<span className="text-sm text-(--color-foreground)">{contact.company ?? t('tenants.detail.notSpecified')}</span>
									{contact.source && (
										<>
											<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.source')}:</span>
											<span className="text-sm text-(--color-foreground)">
												{t(`tenants.detail.sources.${contact.source.toLowerCase()}`, { defaultValue: contact.source })}
											</span>
										</>
									)}
								</div>
							</section>

							<TenantMobileAccessSection contact={contact} t={t} />

							<TenantRentalsSection tenantId={tenantId} t={t} />

							{deal && (
								<section className="flex flex-col gap-3">
									<h2 className="text-base font-[500] leading-6 text-(--color-foreground)">{t('tenants.detail.currentRental')}</h2>
									<div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-2">
										<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.rental')}:</span>
										<span className="text-sm font-[500] text-(--color-info) cursor-pointer underline-offset-2 hover:underline">
											{property ? buildPropertyLabel(property, t) : '—'}
										</span>
										<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.nextPayment')}:</span>
										<span className="text-sm text-(--color-foreground)">{deal.endDate ? formatDate(deal.endDate) : '—'}</span>
										<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.contractNumber')}:</span>
										<span className="text-sm text-(--color-foreground)">№{deal.id.slice(-7)}</span>
										<span className="text-sm text-(--color-muted-foreground)">{t('tenants.detail.rentalPeriod')}:</span>
										<span className="text-sm text-(--color-foreground)">
											{deal.startDate && deal.endDate
												? `${formatDate(deal.startDate)} - ${formatDate(deal.endDate)}`
												: '—'}
										</span>
									</div>
								</section>
							)}
						</div>
					)}

					{tab === 'history' && (
						<TenantHistorySection
							deals={tenantDeals}
							propertiesMap={propertiesMap}
							t={t}
						/>
					)}
					{tab === 'documents' && (
						<TenantDocumentsSection
							tenantId={tenantId}
							deals={tenantDeals}
							propertiesMap={propertiesMap}
							t={t}
						/>
					)}
					{tab === 'notes' && (
						<TenantNotesSection tenantId={tenantId} />
					)}
					{tab === 'kyc' && (
						<TenantKycSection contactId={tenantId} />
					)}
				</div>

				<div className="overflow-hidden">
					{deal && !chatCollapsed && (
						<ConversationChat dealId={deal.id} onCollapse={() => setChatCollapsed(true)} />
					)}
					{deal && chatCollapsed && (
						<div className="flex justify-end pt-1">
							<button
								type="button"
								onClick={() => setChatCollapsed(false)}
								className="flex items-center gap-1.5 rounded-(--radius-md) border border-(--color-border) bg-(--color-background) px-3 py-2 text-sm font-[500] text-(--color-foreground) shadow-(--shadow-sm) transition-colors hover:bg-(--color-muted)"
							>
								<ChevronLeftIcon className="size-4" />
								<span>{t('tenants.detail.chat')}</span>
								{deal.messageCount != null && deal.messageCount > 0 && (
									<span className="inline-flex min-w-5 items-center justify-center rounded-full bg-(--color-destructive) px-1.5 text-xs leading-4 text-(--color-destructive-foreground)">
										{deal.messageCount}
									</span>
								)}
							</button>
						</div>
					)}
				</div>
			</div>

			<StartRentalDialog
				open={startRentalOpen}
				onOpenChange={setStartRentalOpen}
				preset={{ contactId: tenantId }}
			/>

			<EditContactDialog contact={contact} open={editOpen} onOpenChange={setEditOpen} />
			<ChangeReliabilityDialog
				open={statusOpen}
				onOpenChange={setStatusOpen}
				tenantId={tenantId}
				currentStatus={contact.status}
			/>

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title={t('tenants.deleteDialog.title')}
				description={t('tenants.deleteDialog.description', { name: contact.name ?? '' })}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('tenants.actions.delete')}
				variant="destructive"
				onConfirm={async () => {
					try {
						await deleteContact(tenantId).unwrap()
						void navigate({ to: '/tenants' })
					} catch (err) {
						console.error('deleteContact failed:', err)
					}
				}}
			/>
		</div>
	)
}
